import {
  fromIso8601DateTime,
  isValidDate,
  toIso8601DateTime,
} from "@/shared/lib/datetime";
import { useLoggingEffect } from "@/shared/lib/react-hooks";
import { Stack, Typography, useTheme } from "@mui/material";
import { type ForwardedRef, forwardRef, useState } from "react";
import { BaseDatePicker } from "../base-date-picker";
import { BaseTimeField } from "../base-time-field";

export type BaseDateTimePickerProps = {
  value?: string | undefined;
  error?: boolean;
  helperText?: string;
  onChange?: (value: string | undefined) => void;
};
export const BaseDateTimePicker = forwardRef(
  (
    { value, error, helperText, onChange }: BaseDateTimePickerProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const theme = useTheme();
    const [date, setDate] = useState<Date | undefined>();
    const [time, setTime] = useState<Date | undefined>();
    useLoggingEffect(
      () => {
        setDate(value ? fromIso8601DateTime(value) : undefined);
        setTime(value ? fromIso8601DateTime(value) : undefined);
      },
      [value],
      "BaseDateTimePicker",
    );

    const conbineDateAndTime = (date: Date, time: Date) => {
      const dateTime = new Date(date);
      dateTime.setHours(time.getHours());
      dateTime.setMinutes(time.getMinutes());
      dateTime.setSeconds(time.getSeconds());
      return dateTime;
    };
    const handleDateChange = (newDate: Date | undefined) => {
      if (!onChange) {
        return;
      }
      setDate(newDate);
      if (isValidDate(newDate) && isValidDate(time)) {
        onChange(toIso8601DateTime(conbineDateAndTime(newDate, time)));
      }
    };
    const handleTimeChange = (newTime: Date | undefined) => {
      if (!onChange) {
        return;
      }
      setTime(newTime);
      if (isValidDate(date) && isValidDate(newTime)) {
        onChange(toIso8601DateTime(conbineDateAndTime(date, newTime)));
      }
    };
    return (
      <Stack ref={ref} direction="column" spacing={1}>
        <Stack direction="row" spacing={2}>
          <BaseDatePicker value={date} onChange={handleDateChange} />
          <BaseTimeField value={time} onChange={handleTimeChange} />
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: error ? theme.palette.caution[500] : theme.palette.grey[500],
          }}
        >
          {helperText}
        </Typography>
      </Stack>
    );
  },
);
